import { IFormGroupItem, IFormNode } from '@components/Form/Form';
import { IFormItem } from '@components/Form/FormItem/FormItem';
import _ from 'lodash';

import DateMapper, { DateTimeFormat } from '@/utils/dateUtils';

export const isGroupNode = (node: IFormNode): node is IFormGroupItem => {
  return node.type == 'group';
};

export const formSchemaFlatten = (schema: IFormNode[]): IFormItem[] => {
  const result: IFormItem[] = [];

  schema.forEach((node) => {
    if (isGroupNode(node)) {
      result.push(...node.items);
    } else {
      result.push(node);
    }
  });

  return result;
};

export const formInitialValues = (
  data: Record<string, any>,
  schema: IFormItem[],
): Record<string, any> => {
  const initialValues: Record<string, any> = {};

  schema.forEach((field) => {
    const path = field.path ?? field.key;

    let value, processedValue;

    switch (field.type) {
      case 'date':
        value = _.get(data, path, field.default);
        processedValue = value ? DateMapper(value, DateTimeFormat.LOCAL_DATE) : value;
        break;
      case 'time':
        value = _.get(data, path, field.default);
        processedValue = value
          ? DateMapper(value, DateTimeFormat.LOCAL_DATE_TIME)
          : value;
        break;
      case 'dateRange': {
        let fromValue, toValue;

        if (_.isArray(path)) {
          fromValue = _.get(data, [...path.slice(0, -1), `${path[path.length - 1]}From`]);
          toValue = _.get(data, [...path.slice(0, -1), `${path[path.length - 1]}To`]);
        } else {
          fromValue = _.get(data, `start${_.upperFirst(path)}`);
          toValue = _.get(data, `end${_.upperFirst(path)}`);
        }

        const processedFrom = fromValue
          ? DateMapper(fromValue, DateTimeFormat.LOCAL_DATE)
          : undefined;
        const processedTo = toValue
          ? DateMapper(toValue, DateTimeFormat.LOCAL_DATE)
          : undefined;

        processedValue = [processedFrom, processedTo];
        break;
      }
      case 'numberRange': {
        let fromValue, toValue;

        if (_.isArray(path)) {
          fromValue = _.get(data, [...path.slice(0, -1), `${path[path.length - 1]}From`]);
          toValue = _.get(data, [...path.slice(0, -1), `${path[path.length - 1]}To`]);
        } else {
          fromValue = _.get(data, `${path}From`);
          toValue = _.get(data, `${path}To`);
        }

        processedValue = [fromValue, toValue];
        break;
      }
      case 'numberRangeExtra': {
        let fromValue, toValue, extraValue;

        if (_.isArray(path)) {
          fromValue = _.get(data, [...path.slice(0, -1), `${path[path.length - 1]}From`]);
          toValue = _.get(data, [...path.slice(0, -1), `${path[path.length - 1]}To`]);
          extraValue = _.get(data, [...path.slice(0, -1), field.payload?.extraPath]);
        } else {
          fromValue = _.get(data, `${path}From`);
          toValue = _.get(data, `${path}To`);
          extraValue = _.get(data, field.payload?.extraPath);
        }

        processedValue = [[fromValue, toValue], extraValue];
        break;
      }
      default:
        value = _.get(data, path, field.default);
        processedValue = value;
    }

    _.set(initialValues, path, processedValue);
  });

  return initialValues;
};

export const formFinishValues = (
  values: Record<string, any>,
  schema: IFormItem[],
): Record<string, any> => {
  const finishValues: Record<string, any> = {};

  schema.forEach((field) => {
    const path = field.path ?? field.key;
    const value = _.get(values, path);

    switch (field.type) {
      case 'date': {
        const formatted = value ? value.format(DateTimeFormat.LOCAL_DATE) : value;
        _.set(finishValues, path, formatted);
        break;
      }
      case 'time': {
        const formatted = value ? value.format(DateTimeFormat.TIME) : value;
        _.set(finishValues, path, formatted);
        break;
      }
      case 'dateRange': {
        if (_.isArray(value)) {
          const formattedFrom = value[0]
            ? field.payload?.formatStartOnSubmit
              ? field.payload.formatStartOnSubmit(value[0])
              : value[0].format(DateTimeFormat.LOCAL_DATE)
            : value[0];
          const formattedTo = value[1]
            ? field.payload?.formatEndOnSubmit
              ? field.payload.formatEndOnSubmit(value[1])
              : value[1].format(DateTimeFormat.LOCAL_DATE)
            : value[1];
          let pathFrom, pathTo;

          if (_.isArray(path)) {
            pathFrom = [...path.slice(0, -1), path[path.length - 1] + 'From'];
            pathTo = [...path.slice(0, -1), path[path.length - 1] + 'To'];
          } else {
            pathFrom = `start${_.upperFirst(path)}`;
            pathTo = `end${_.upperFirst(path)}`;
          }

          _.set(finishValues, pathFrom, formattedFrom);
          _.set(finishValues, pathTo, formattedTo);
        }
        break;
      }
      case 'numberRange': {
        if (_.isArray(value)) {
          let pathFrom, pathTo;

          if (_.isArray(path)) {
            pathFrom = [...path.slice(0, -1), path[path.length - 1] + 'From'];
            pathTo = [...path.slice(0, -1), path[path.length - 1] + 'To'];
          } else {
            pathFrom = `${path}From`;
            pathTo = `${path}To`;
          }

          _.set(finishValues, pathFrom, value[0]);
          _.set(finishValues, pathTo, value[1]);
        }
        break;
      }
      case 'numberRangeExtra': {
        if (_.isArray(value)) {
          const [range, extra] = value;
          let pathFrom, pathTo, pathExtra;

          if (_.isArray(path)) {
            pathFrom = [...path.slice(0, -1), path[path.length - 1] + 'From'];
            pathTo = [...path.slice(0, -1), path[path.length - 1] + 'To'];
            pathExtra = [...path.slice(0, -1), field.payload?.extraPath];
          } else {
            pathFrom = `${path}From`;
            pathTo = `${path}To`;
            pathExtra = field.payload?.extraPath;
          }

          _.set(finishValues, pathFrom, range?.[0]);
          _.set(finishValues, pathTo, range?.[1]);
          _.set(finishValues, pathExtra, extra);
        }
        break;
      }
      case 'file': {
        const fileList = _.isArray(value) ? value : value?.fileList;
        const formatted = fileList?.[0]?.originFileObj;
        _.set(finishValues, path, formatted);
        break;
      }
      case 'label':
        break;
      default:
        _.set(finishValues, path, value);
    }
  });

  return finishValues;
};
